import { useContext, useState } from "react";
import { NewsletterContext } from "../context/NewsletterContext";
import { parseTopics } from "@/utils/textRelated";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Hardcoded topic pills data with short, user-friendly descriptions
const TOPIC_PILLS = [
  {
    id: "Products & Applications",
    label: "Products & Applications",
    description: "New AI tools and features people can actually use.",
  },
  {
    id: "Business & Investments",
    label: "Business & Investments",
    description: "Funding, deals, partnerships, and market moves.",
  },
  {
    id: "Policy & Ethics",
    label: "Policy & Ethics",
    description: "Rules, risks, and responsible AI conversations.",
  },
  {
    id: "Work & Society",
    label: "Work & Society",
    description: "How AI affects jobs, schools, health, and daily life.",
  },
  {
    id: "Research & Models",
    label: "Research & Models",
    description: "Breakthroughs in AI models, methods, and benchmarks.",
  },
  {
    id: "Hardware & Infrastructure",
    label: "Hardware & Infrastructure",
    description: "Chips, GPUs, and data centers powering AI.",
  },
];

// (No separate definitions map; descriptions live on each pill)

export const SetTopic = () => {
  const {
    config: { topics },
    updateTopics,
    updateTone,
    preview,
    config,
    updatePreview,
  } = useContext(NewsletterContext);

  const [query, setQuery] = useState(() => {
    return topics?.length ? topics.join(", ") : "";
  });

  const [selectedPills, setSelectedPills] = useState<string[]>([]);

  const handleTopicsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const parsedTopics = parseTopics(e.target.value);

    // Just update topics - embedding will be generated when they click Next
    updateTopics(parsedTopics);

    updatePreview({
      subject: "",
      body: "",
      articles: [],
    });
    updateTone(null, "");
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= 120) {
      setQuery(newValue);
    }
    handleTopicsChange(e);
  };

  const handlePillClick = (pillLabel: string) => {
    const isSelected = selectedPills.includes(pillLabel);

    if (isSelected) {
      // Remove pill
      const newSelectedPills = selectedPills.filter((p) => p !== pillLabel);
      setSelectedPills(newSelectedPills);

      // Update textarea by removing this topic
      const currentTopics = query
        .split(", ")
        .filter((topic) => topic.trim() !== pillLabel);
      const newQuery = currentTopics.join(", ");
      setQuery(newQuery);

      // Update topics in context
      const parsedTopics = parseTopics(newQuery);
      updateTopics(parsedTopics);
    } else {
      // Add pill
      const newSelectedPills = [...selectedPills, pillLabel];
      setSelectedPills(newSelectedPills);

      // Update textarea by appending this topic
      const currentTopics = query
        .split(", ")
        .filter((topic) => topic.trim() !== "");
      const newTopics = [...currentTopics, pillLabel];
      const newQuery = newTopics.join(", ");
      setQuery(newQuery);

      // Update topics in context
      const parsedTopics = parseTopics(newQuery);
      updateTopics(parsedTopics);
    }
  };

  console.log("Preview:", preview);
  console.log("Config:", config);
  return (
    <div
      className="mb-12 border border-black rounded-xl overflow-hidden shadow-md"
      style={{
        background:
          "linear-gradient(135deg, #e0f2fe 0%, #dbeafe 50%, #ede9fe 100%)",
      }}
    >
      <div
        className="p-6 border-b border-black"
        style={{
          background: "linear-gradient(135deg, #bfdbfe 0%, #c7d2fe 100%)",
        }}
      >
        <h2 className="text-lg font-medium mb-2 ">
          Which AI topics have you been following?
        </h2>
      </div>
      <div className="p-6">
        <textarea
          value={query}
          onChange={handleChange}
          placeholder="Tools that help me code, How AI affects my job, AI in healthcare, etc."
          className="w-full p-4 h-32 text-base bg-white border border-black rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          maxLength={240}
        />
        <div className="mt-2 text-sm text-gray-600 text-right">
          {240 - query.length} characters remaining
        </div>

        {/* Topic Pills */}
        <div className="mt-4">
          <p className="text-xs font-medium text-gray-600 mb-2">
            Or choose from popular topics:
          </p>
          <TooltipProvider delayDuration={100} skipDelayDuration={0}>
            <div className="flex flex-wrap gap-2">
              {TOPIC_PILLS.map((pill) => {
                const isSelected = selectedPills.includes(pill.label);
                return (
                  <Tooltip key={pill.id}>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => handlePillClick(pill.label)}
                        className={`px-3 py-2 rounded-full text-xs font-medium transition-all duration-300 border-2 ${
                          isSelected
                            ? "bg-black text-white border-black shadow-md transform scale-105"
                            : "bg-white text-gray-700 border-gray-300 hover:border-gray-400 hover:shadow-sm hover:scale-105 active:scale-95"
                        }`}
                      >
                        {pill.label}
                      </button>
                    </TooltipTrigger>
                    {pill.description && (
                      <TooltipContent className="w-fit leading-snug text-gray-800">
                        {pill.description}
                      </TooltipContent>
                    )}
                  </Tooltip>
                );
              })}
            </div>
          </TooltipProvider>
        </div>
      </div>
    </div>
  );
};
